import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ArrowLeft, Lock, LogOut, LayoutGrid, CheckCircle, CalendarDays, List, Plus, Mail, BarChart3, Gift, Settings, MessageSquare, Image, Activity } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import extractErrorMessage from "@/utils/extractErrorMessage";
import { configureAdminAxios, clearAdminAxios } from "@/utils/authAxios";
import { generateQuotePDF } from "@/utils/quotePDF";
import { generateInvoicePDF } from "@/utils/invoicePDF";
import { KanbanBoard } from "@/components/admin/KanbanBoard";
import { ConfirmedDates } from "@/components/admin/ConfirmedDates";
import { CalendarView } from "@/components/admin/CalendarView";
import { ListView } from "@/components/admin/ListView";
import { BookingManager } from "@/components/admin/BookingManager";
import { EditBookingDialog } from "@/components/admin/EditBookingDialog";
import { AddBookingDialog } from "@/components/admin/AddBookingDialog";
import { Dashboard } from "@/components/admin/Dashboard";
import { PromotionsManager } from "@/components/admin/PromotionsManager";
import { ReviewsManager } from "@/components/admin/ReviewsManager";
import { PhotosManager } from "@/components/admin/PhotosManagerAdvanced";
import { TrackingManager } from "@/components/admin/TrackingManager";
import { CalendarManager } from "@/components/admin/CalendarManager";
import AdminSettingsPage from "./AdminSettingsPage";
import { getBackendUrl } from "@/utils/apiBase";

const BACKEND_URL = getBackendUrl();
const API = `${BACKEND_URL}/api`;

const AdminPage = () => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [email, setEmail] = useState("walid.societe.pro@gmail.com");
  const [password, setPassword] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeView, setActiveView] = useState("bookings");
  const [searchTerm, setSearchTerm] = useState("");
  const [editingBooking, setEditingBooking] = useState(null);
  const [addBookingDialogOpen, setAddBookingDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [bookingToDelete, setBookingToDelete] = useState(null);
  const [activePromotion, setActivePromotion] = useState(null);

  // 2FA OTP state
  const [otpPhase, setOtpPhase] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const [tempToken, setTempToken] = useState("");

  useEffect(() => {
    const token = localStorage.getItem("admin_token");
    if (token) {
      configureAdminAxios(token);
      verifyToken(token);
    }
  }, []);

  // Listen for booking updates from children (e.g. mark-signed in BookingManager)
  useEffect(() => {
    const handler = () => { if (isAuthenticated) fetchBookings(); };
    window.addEventListener("booking-updated", handler);
    return () => window.removeEventListener("booking-updated", handler);
  }, [isAuthenticated]);

  const verifyToken = async (token) => {
    try {
      const response = await axios.get(`${API}/admin/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data) {
        configureAdminAxios(token);
        setIsAuthenticated(true);
        fetchBookings();
        fetchActivePromotion();
      }
    } catch (error) {
      // 401 = expired/invalid token → silent cleanup (expected).
      // Only log real errors (network, 500, etc.)
      if (error?.response?.status !== 401) {
        console.error("Token verification failed:", error);
      }
      localStorage.removeItem("admin_token");
      clearAdminAxios();
      setIsAuthenticated(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);

    try {
      const response = await axios.post(`${API}/admin/login`, { email, password });

      if (response.data.requires_otp) {
        // Phase 1 OK — show OTP input
        setTempToken(response.data.temp_token);
        setOtpPhase(true);
        setOtpCode("");
        toast.success("Code de vérification envoyé par email");
      } else if (response.data.token) {
        // Direct token (shouldn't happen with 2FA but fallback)
        localStorage.setItem("admin_token", response.data.token);
        configureAdminAxios(response.data.token);
        setIsAuthenticated(true);
        toast.success("Connexion réussie");
        fetchBookings();
        fetchActivePromotion();
      }
    } catch (error) {
      toast.error(extractErrorMessage(error, "Erreur de connexion"));
    } finally {
      setLoginLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setLoginLoading(true);

    try {
      const response = await axios.post(`${API}/admin/verify-otp`, {
        temp_token: tempToken,
        otp_code: otpCode,
      });

      if (response.data.token) {
        localStorage.setItem("admin_token", response.data.token);
        configureAdminAxios(response.data.token);
        setIsAuthenticated(true);
        setOtpPhase(false);
        setOtpCode("");
        setTempToken("");
        toast.success("Connexion réussie");
        fetchBookings();
        fetchActivePromotion();
      }
    } catch (error) {
      toast.error(extractErrorMessage(error, "Code invalide"));
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("admin_token");
    clearAdminAxios();
    setIsAuthenticated(false);
    toast.success("Déconnexion réussie");
    navigate("/");
  };

  const fetchActivePromotion = async () => {
    try {
      const response = await axios.get(`${API}/promotions/active`);
      if (response.data) {
        setActivePromotion(response.data);
      }
    } catch (error) {
      console.error("Erreur lors du chargement de la promotion active", error);
    }
  };

  const fetchBookings = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/bookings`);
      setBookings(response.data.bookings);
    } catch (error) {
      console.error("Erreur lors du chargement des réservations", error);
      toast.error("Erreur lors du chargement des réservations");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!bookingToDelete) return;
    try {
      await axios.delete(`${API}/bookings/${bookingToDelete.id}`);
      toast.success("Réservation supprimée avec succès");
      fetchBookings();
      setDeleteDialogOpen(false);
      setBookingToDelete(null);
    } catch (error) {
      console.error("Erreur lors de la suppression", error);
      toast.error("Erreur lors de la suppression de la réservation");
    }
  };

  const handleUpdate = async (bookingId, updatedData) => {
    try {
      await axios.put(`${API}/bookings/${bookingId}`, updatedData);
      toast.success("Réservation mise à jour avec succès");
      fetchBookings();
      fetchActivePromotion(); // Refresh promotion in case it changed
      setEditingBooking(null);
    } catch (error) {
      console.error("Erreur lors de la mise à jour", error);
      toast.error("Erreur lors de la mise à jour de la réservation");
    }
  };

  // Calculate price with promotion
  const calculatePriceWithPromotion = (booking) => {
    const originalPrice = booking.total_price || 0;
    const promotion = booking.promotion || activePromotion;
    
    if (!promotion || !promotion.value) {
      return { original: originalPrice, final: originalPrice, discount: 0 };
    }

    let discount = 0;
    if (promotion.type === 'percentage' || promotion.discount_type === 'percentage') {
      discount = (originalPrice * promotion.value) / 100;
    } else {
      discount = promotion.value;
    }

    return {
      original: originalPrice,
      final: originalPrice - discount,
      discount: discount,
      promotion: promotion
    };
  };

  const handleAddBooking = async (bookingData) => {
    try {
      await axios.post(`${API}/admin/bookings`, bookingData);
      toast.success("Réservation créée ✓");
      fetchBookings();
      setAddBookingDialogOpen(false);
    } catch (error) {
      const detail = error?.response?.data?.detail || "Erreur lors de l'ajout de la réservation";
      console.error("Erreur lors de l'ajout de la réservation", error);
      toast.error(typeof detail === "string" ? detail : "Erreur lors de l'ajout de la réservation");
      throw error; // let the dialog know
    }
  };

  const handleGenerateQuote = async (booking) => {
    try {
      await generateQuotePDF(booking);
      // Save to history
      await axios.post(`${API}/bookings/${booking.id}/documents?document_type=quote`);
      toast.success("Devis généré avec succès !");
    } catch (error) {
      console.error("Erreur lors de la génération du devis", error);
      toast.error("Erreur lors de la génération du devis");
    }
  };

  const handleGenerateInvoice = async (booking) => {
    try {
      await generateInvoicePDF(booking);
      // Save to history
      await axios.post(`${API}/bookings/${booking.id}/documents?document_type=invoice`);
      toast.success("Facture générée avec succès !");
    } catch (error) {
      console.error("Erreur lors de la génération de la facture", error);
      toast.error("Erreur lors de la génération de la facture");
    }
  };

  const handleTestEmail = async () => {
    try {
      const response = await axios.post(`${API}/test-email`);
      toast.success(response.data.message);
    } catch (error) {
      console.error("Erreur lors du test d'email", error);
      toast.error("Erreur lors du test d'email");
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 bg-[#faf8f5]">
        <Card className="w-full max-w-md border-2 border-gray-200 shadow-xl">
          <CardHeader className="bg-[#fef9f5] border-b border-[#f0e6d3]">
            <div className="flex items-center justify-center mb-4">
              <Lock className="h-12 w-12 text-[#b8977e]" />
            </div>
            <CardTitle className="text-2xl text-center">Espace Administrateur</CardTitle>
            <CardDescription className="text-center">
              {otpPhase ? "Entrez le code reçu par email" : "Connectez-vous pour gérer les réservations"}
            </CardDescription>
          </CardHeader>
          <CardContent className="p-6">
            {!otpPhase ? (
              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input 
                    id="email" 
                    type="email" 
                    value={email} 
                    onChange={(e) => setEmail(e.target.value)} 
                    placeholder="admin@example.com" 
                    className="mt-1"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="password">Mot de passe</Label>
                  <Input 
                    id="password" 
                    type="password" 
                    value={password} 
                    onChange={(e) => setPassword(e.target.value)} 
                    placeholder="Entrez votre mot de passe" 
                    className="mt-1" 
                    data-testid="admin-password-input"
                    required
                  />
                </div>
                <Button 
                  type="submit" 
                  className="w-full bg-[#b8977e] hover:bg-[#a68770]" 
                  data-testid="admin-login-btn"
                  disabled={loginLoading}
                >
                  {loginLoading ? "Envoi du code..." : "Se connecter"}
                </Button>
                <Button type="button" variant="outline" className="w-full" onClick={() => navigate("/")}>
                  Retour à l'accueil
                </Button>
              </form>
            ) : (
              <form onSubmit={handleVerifyOtp} className="space-y-4">
                <div className="text-center mb-2">
                  <Mail className="h-10 w-10 text-[#b8977e] mx-auto mb-2" />
                  <p className="text-sm text-gray-600">
                    Un code à 6 chiffres a été envoyé à <strong>{email}</strong>
                  </p>
                </div>
                <div>
                  <Label htmlFor="otp">Code de vérification</Label>
                  <Input 
                    id="otp" 
                    type="text" 
                    inputMode="numeric"
                    maxLength={6}
                    value={otpCode} 
                    onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6))} 
                    placeholder="000000" 
                    className="mt-1 text-center text-2xl tracking-[0.5em] font-bold"
                    data-testid="admin-otp-input"
                    autoFocus
                    required
                  />
                </div>
                <Button 
                  type="submit" 
                  className="w-full bg-[#b8977e] hover:bg-[#a68770]" 
                  data-testid="admin-otp-verify-btn"
                  disabled={loginLoading || otpCode.length !== 6}
                >
                  {loginLoading ? "Vérification..." : "Vérifier le code"}
                </Button>
                <Button 
                  type="button" 
                  variant="outline" 
                  className="w-full" 
                  onClick={() => { setOtpPhase(false); setOtpCode(""); setTempToken(""); }}
                >
                  Retour au login
                </Button>
                <p className="text-xs text-gray-500 text-center">Le code expire dans 10 minutes</p>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 px-4 sm:px-6 lg:px-8 bg-[#faf8f5]">
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <div className="flex items-center gap-4">
            <Button onClick={() => navigate("/")} variant="ghost" className="hover:bg-rose-50">
              <ArrowLeft className="mr-2 h-4 w-4" />Retour
            </Button>
            <h1 className="text-3xl font-bold text-gray-900">Gestion Admin</h1>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={() => setAddBookingDialogOpen(true)}
              className="bg-[#b8977e] hover:bg-[#a68770] text-white"
            >
              <Plus className="mr-2 h-4 w-4" />
              Ajouter une Réservation
            </Button>
            <Button
              onClick={handleTestEmail}
              variant="outline"
              className="border-blue-500 text-blue-600 hover:bg-blue-50"
            >
              <Mail className="mr-2 h-4 w-4" />
              Tester Email
            </Button>
            <Button onClick={handleLogout} variant="outline" className="border-red-500 text-red-600 hover:bg-red-50" data-testid="admin-logout-btn">
              <LogOut className="mr-2 h-4 w-4" />Déconnexion
            </Button>
          </div>
        </div>

        {/* Active Promotion Banner */}
        {activePromotion && (
          <Card className="mb-6 border-2 border-[#b8977e]/40 bg-[#fef9f5] shadow-lg">
            <CardContent className="p-4">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="bg-[#b8977e] text-white p-2 rounded-full">
                    <Gift className="h-6 w-6" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-[#b8977e]">Promotion Active</h3>
                    <p className="text-sm text-gray-700">
                      <span className="font-semibold">{activePromotion.title}</span>
                      {" - "}
                      {activePromotion.discount_type === 'percentage' 
                        ? `${activePromotion.discount_value}% de réduction` 
                        : `${activePromotion.discount_value}€ de réduction`}
                    </p>
                    <p className="text-xs text-gray-600">
                      Valide du {new Date(activePromotion.start_date).toLocaleDateString('fr-FR')} 
                      {" au "} 
                      {new Date(activePromotion.end_date).toLocaleDateString('fr-FR')}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="text-right">
                    <p className="text-2xl font-bold text-[#b8977e]">
                      {activePromotion.discount_type === 'percentage' 
                        ? `-${activePromotion.discount_value}%` 
                        : `-${activePromotion.discount_value}€`}
                    </p>
                    <p className="text-xs text-gray-600">sur tous les forfaits</p>
                  </div>
                  <Button 
                    onClick={() => setActiveView("promotions")} 
                    size="sm" 
                    variant="outline"
                    className="border-[#b8977e] text-[#b8977e] hover:bg-[#fef9f5]"
                  >
                    Gérer
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        <Card className="mb-6 border-2 shadow-lg">
          <CardContent className="p-4">
            <div className="flex flex-wrap gap-2">
              <Button variant={activeView === "dashboard" ? "default" : "outline"} onClick={() => setActiveView("dashboard")} className={activeView === "dashboard" ? "bg-[#b8977e] hover:bg-[#a68770]" : ""}>
                <BarChart3 className="mr-2 h-4 w-4" />Dashboard
              </Button>
              <Button variant={activeView === "promotions" ? "default" : "outline"} onClick={() => setActiveView("promotions")} className={activeView === "promotions" ? "bg-[#b8977e] hover:bg-[#a68770]" : ""}>
                <Gift className="mr-2 h-4 w-4" />Promotions
              </Button>
              <Button variant={activeView === "reviews" ? "default" : "outline"} onClick={() => setActiveView("reviews")} className={activeView === "reviews" ? "bg-[#b8977e] hover:bg-[#a68770]" : ""}>
                <MessageSquare className="mr-2 h-4 w-4" />Avis Clients
              </Button>
              <Button variant={activeView === "photos" ? "default" : "outline"} onClick={() => setActiveView("photos")} className={activeView === "photos" ? "bg-[#b8977e] hover:bg-[#a68770]" : ""}>
                <Image className="mr-2 h-4 w-4" />Photos
              </Button>
              <Button variant={activeView === "tracking" ? "default" : "outline"} onClick={() => setActiveView("tracking")} className={activeView === "tracking" ? "bg-[#b8977e] hover:bg-[#a68770]" : ""}>
                <Activity className="mr-2 h-4 w-4" />Marketing
              </Button>
              <Button variant={activeView === "bookings" ? "default" : "outline"} onClick={() => setActiveView("bookings")} className={activeView === "bookings" ? "bg-[#b8977e] hover:bg-[#a68770]" : ""}>
                <LayoutGrid className="mr-2 h-4 w-4" />Réservations
              </Button>
              <Button variant={activeView === "confirmed-dates" ? "default" : "outline"} onClick={() => setActiveView("confirmed-dates")} className={activeView === "confirmed-dates" ? "bg-[#b8977e] hover:bg-[#a68770]" : ""}>
                <CheckCircle className="mr-2 h-4 w-4" />Dates Confirmées
              </Button>
              <Button variant={activeView === "calendar-blocking" ? "default" : "outline"} onClick={() => setActiveView("calendar-blocking")} className={activeView === "calendar-blocking" ? "bg-[#b8977e] hover:bg-[#a68770]" : ""} data-testid="nav-calendar-blocking">
                <CalendarDays className="mr-2 h-4 w-4" />Calendrier
              </Button>
              <Button variant={activeView === "settings" ? "default" : "outline"} onClick={() => setActiveView("settings")} className={activeView === "settings" ? "bg-[#b8977e] hover:bg-[#a68770]" : ""}>
                <Settings className="mr-2 h-4 w-4" />Paramètres
              </Button>
            </div>
          </CardContent>
        </Card>

        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#b8977e]"></div>
          </div>
        ) : (
          <>
            {activeView === "dashboard" && <Dashboard />}
            {activeView === "promotions" && <PromotionsManager />}
            {activeView === "reviews" && <ReviewsManager />}
            {activeView === "photos" && <PhotosManager />}
            {activeView === "tracking" && <TrackingManager />}
            {activeView === "settings" && <AdminSettingsPage />}
            {activeView === "bookings" && <BookingManager bookings={bookings} onRefresh={fetchBookings} onEdit={setEditingBooking} onDelete={(booking) => { setBookingToDelete(booking); setDeleteDialogOpen(true); }} onGenerateQuote={handleGenerateQuote} onGenerateInvoice={handleGenerateInvoice} activePromotion={activePromotion} calculatePriceWithPromotion={calculatePriceWithPromotion} />}
            {activeView === "confirmed-dates" && <ConfirmedDates bookings={bookings} />}
            {activeView === "calendar-blocking" && <CalendarManager />}
          </>
        )}
      </div>

      {editingBooking && <EditBookingDialog booking={editingBooking} onClose={() => setEditingBooking(null)} onSave={handleUpdate} onGenerateQuote={handleGenerateQuote} onGenerateInvoice={handleGenerateInvoice} />}

      <AddBookingDialog open={addBookingDialogOpen} onClose={() => setAddBookingDialogOpen(false)} onSave={handleAddBooking} />

      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmer la suppression</DialogTitle>
            <DialogDescription>Êtes-vous sûr de vouloir supprimer la réservation de {bookingToDelete?.customer_name} ? Cette action est irréversible.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>Annuler</Button>
            <Button variant="destructive" onClick={handleDelete} data-testid="confirm-delete-btn">Supprimer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPage;